import React, {useState} from 'react';
import {classNames} from '@utils/class-names';
import {ReactComponent as ChevronDownIcon} from '@assets/icons/chevron-down.svg';
import {ReactComponent as CheckmarkIcon} from '@assets/icons/checkmark.svg';
import AppButton from "@common/components/app-button";
import Popover from "@common/components/popover";
import {ArticleListRequestParams} from "../../core/models/interfaces/article-list-request-params.interface";

type SortBy = 'title' | 'updatedAt' | 'visibility';

interface ArticleSorterProps {
    onSelect?: (value: ArticleListRequestParams) => void;
    className?: string;
    searchParams?: ArticleListRequestParams;
}

const ArticleSorter: React.FC<ArticleSorterProps> = (props) => {
    const {
        onSelect,
        className = '',
        searchParams
    } = props;
    const [selectedValue, setSelectedValue] = useState(searchParams?.sortBy);

    const options: Array<{ label: string, sortBy: SortBy, sortOrder: "asc" | "desc" }> = [
        {label: 'Sortuj od A-Z', sortBy: 'title', sortOrder: "asc"},
        {label: 'Sortuj od najnowszych', sortBy: 'updatedAt', sortOrder: "desc"},
        {label: 'Sortuj po widoczności', sortBy: 'visibility', sortOrder: "asc"},
    ];

    const selectedOption = options.find((opt) => opt.sortBy === selectedValue) || options[0];

    const handleSelect = (value: { label: string, sortBy: SortBy, sortOrder: "asc" | "desc" }) => {
        setSelectedValue(value.sortBy);
        onSelect?.({
            ...searchParams,
            sortBy: value.sortBy,
            sortOrder: value.sortOrder,
        });
    };

    const dropdownContent = (
        <div className="w-fit bg-white rounded-2xl shadow-lvl-1 border border-outline-mild overflow-hidden">
            <div className="py-2">
                {options.map((option) => {
                    const isSelected = option.sortBy === selectedValue;

                    return (
                        <button
                            key={option.sortBy}
                            onClick={() => handleSelect(option)}
                            className={classNames(
                                'flex items-center w-full px-4 py-2 text-sm text-on-surface pr-[56px]',
                                'hover:bg-on-surface/5 transition-colors duration-150 whitespace-nowrap text-left',
                                isSelected ? 'bg-success/10 hover:!bg-success/20 font-semibold' : ''
                            )}
                            role="menuitem"
                        >
                            {isSelected ? (
                                <CheckmarkIcon className="w-4 h-4 mr-3 text-primary"/>
                            ) : (
                                <span className="min-w-4 w-4 h-4 mr-3"/> // Placeholder for alignment
                            )}
                            {option.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );

    return (
        <div className={classNames('w-fit', className)}>
            <Popover
                content={dropdownContent}
                preferredPosition="bottom"
                offset={4}
            >
                <AppButton
                    type="button"
                    className="bg-surface-level-1 w-fit items-center justify-center max-h-[32px] rounded-lg border border-outline !py-0 h-[32px]"
                >
                    <div className="flex items-center justify-center">
                        <span className="mr-2 text-sm font-semibold">{selectedOption?.label}</span>
                        <ChevronDownIcon className="transition-transform duration-200"/>
                    </div>
                </AppButton>
            </Popover>
        </div>
    );
};

export default ArticleSorter;
